import type { ReactNode } from 'react';
import { Dialog, type DialogProps } from '@mui/material';
import type { DialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { DialogGuardBody, type DiscardCopy } from './DialogGuardBody';
import { withDialogSubtitle } from './DialogSubtitle';

/**
 * Content the editor is showing instead of its form, in the same dialog: a confirmation
 * (`ConfirmPanel`), or a sub-task such as the rule editor's dry run (which has no consequence text).
 */
export interface InPlaceConfirm {
  titleId: string;
  consequenceId?: string | undefined;
  /** `Esc` / backdrop dismiss it; `undefined` while it is busy. */
  onClose: (() => void) | undefined;
}

type GuardedDialogProps = Omit<DialogProps, 'open' | 'onClose' | 'aria-labelledby'> & {
  /** Defaults to open — most editors mount only while they are being used. */
  open?: boolean | undefined;
  guard: DialogDirtyGuard;
  /** The form title's id — the dialog's accessible name while the form is showing. */
  titleId?: string | undefined;
  /**
   * The editor swapped its form for a confirmation (remove, leave): that confirmation owns the
   * dialog's name and its dismissal, not the discard guard.
   */
  confirm?: InPlaceConfirm | null | undefined;
  /** Prototype subtitle under the title. Omitted while a confirmation replaces the form. */
  subtitle?: ReactNode;
  /** Points `aria-describedby` at this id when the form (not a confirmation) is showing. */
  subtitleId?: string;
  /** The discard question's own words (`DialogGuardBody`); the generic ones without. */
  discardCopy?: DiscardCopy | undefined;
};

/**
 * An open editor `Dialog` that guards unsaved work (TMMAPP-0073, TMM-B5): `Esc`, the backdrop,
 * Cancel and a ✕ all go through `guard.requestClose`, and the discard question replaces the
 * form in place (`DialogGuardBody`). Everything else is a plain MUI `Dialog`, and one stable
 * `Dialog` for the editor's whole life — swapping its content never remounts it (DLG-005).
 */
export function GuardedDialog({
  guard,
  titleId,
  open = true,
  confirm = null,
  subtitle,
  subtitleId,
  discardCopy,
  children,
  ...dialogProps
}: GuardedDialogProps) {
  // The discard question outranks an in-place confirmation: a navigation held while the editor sits
  // behind its Remove / Leave confirmation asks in this same dialog, so the dialog takes the
  // question's name and `Esc` is its dismiss (Keep editing), not the confirmation's cancel.
  const hostProps =
    confirm === null || guard.discarding
      ? guard.dialogProps(titleId)
      : {
          onClose: confirm.onClose,
          'aria-labelledby': confirm.titleId,
          ...(confirm.consequenceId !== undefined
            ? { 'aria-describedby': confirm.consequenceId }
            : {}),
        };
  const described =
    subtitleId !== undefined && confirm === null && !guard.discarding
      ? { 'aria-describedby': subtitleId }
      : {};
  // The same keyed tree whether the subtitle shows or not, so the discard question (or a
  // confirmation) never remounts the form under it and Keep editing keeps its drafts
  // (TMMAPP-0154 item 21).
  const body =
    subtitle != null
      ? withDialogSubtitle(children, confirm === null && !guard.discarding ? subtitle : null)
      : children;
  return (
    <Dialog open={open} {...dialogProps} {...described} {...hostProps}>
      <DialogGuardBody guard={guard} copy={discardCopy}>
        {body}
      </DialogGuardBody>
    </Dialog>
  );
}
